import { create } from "zustand";
import { persist, StorageValue } from "zustand/middleware";

export type CartItem = {
  databaseId: number;
  name: string;
  price: number;
  regular_price: number;
  sale_price: number;
  quantity: number;
  available_quantity?: number;
  date?: string;
  shippingClassId?: number;
};

export type CartState = {
  items: CartItem[];
  isLoading: boolean;
  error: string | null;
  discount: TDiscount;
  shippingCost: number;
};

export type TDiscount = {
  type: "percentage" | "fixed" | null;
  value: number;
  code: string | null;
  databaseId: number | null;
};

export type CartActions = {
  addToCart: (item: CartItem) => void;
  removeFromCart: (databaseId: number) => void;
  updateQuantity: (databaseId: number, quantity: number) => void;
  incrementQuantity: (databaseId: number, incrementBy: number) => void;
  decrementQuantity: (databaseId: number, decrementBy: number) => void;
  clearCart: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  applyDiscount: (
    type: "percentage" | "fixed",
    value: number,
    code: string,
    databaseId: number
  ) => void;
  removeDiscount: () => void;
};

export type CartStore = CartState &
  CartActions & {
    orderId: string | null;
    setOrderId: (id: string) => void;
    totalQuantity: () => number; // Derived total quantity
    totalPrice: () => number; // Derived total price
    discountAmount: () => number;
    debug: () => void;
    initializeStore: () => void;
    calculateShipping: () => number;
    totalWithShipping: () => number;
  };

export const createCartStore = () =>
  create<CartStore>()(
    persist(
      (set, get) => ({
        items: [],
        orderId: null,
        isLoading: true,
        error: null,
        discount: {
          type: null,
          value: 0,
          code: null,
          databaseId: null,
        },
        shippingCost: 0,

        initializeStore: () => {
          set({ isLoading: false });
        },

        addToCart: async (newItem) => {
          set({ isLoading: true, error: null });
          try {
            const existingItem = get().items.find(
              (item) => item.databaseId === newItem.databaseId
            );

            if (existingItem) {
              set((state) => {
                const updatedItems = state.items.map((item) =>
                  item.databaseId === newItem.databaseId
                    ? { ...item, quantity: item.quantity + newItem.quantity }
                    : item
                );

                return {
                  items: updatedItems,
                  isLoading: false,
                };
              });
            } else {
              set((state) => {
                const updatedItems = [...state.items, newItem];

                return {
                  items: updatedItems,
                  isLoading: false,
                };
              });
            }

            // Force a state update to recalculate derived values
            setTimeout(() => {
              set((state) => ({ ...state }));
            }, 0);
          } catch (err) {
            set({
              error: err instanceof Error ? err.message : "An error occurred",
              isLoading: false,
            });
          }
        },

        removeFromCart: (databaseId) =>
          set((state) => ({
            items: state.items.filter((item) => item.databaseId !== databaseId),
          })),

        updateQuantity: (databaseId, quantity) =>
          set((state) => ({
            items: state.items.map((item) =>
              item.databaseId === databaseId ? { ...item, quantity } : item
            ),
          })),

        incrementQuantity: (databaseId, incrementBy) =>
          set((state) => ({
            items: state.items.map((item) =>
              item.databaseId === databaseId
                ? { ...item, quantity: item.quantity + incrementBy }
                : item
            ),
          })),

        decrementQuantity: (databaseId, decrementBy) =>
          set((state) => {
            const item = state.items.find(
              (item) => item.databaseId === databaseId
            );

            if (!item) return state; // Item not found, return current state

            const newQuantity = item.quantity - decrementBy;

            if (newQuantity <= 0) {
              // Use removeFromCart if quantity is 0 or less
              return {
                items: state.items.filter(
                  (item) => item.databaseId !== databaseId
                ),
              };
            }

            // Update quantity if greater than 0
            return {
              items: state.items.map((item) =>
                item.databaseId === databaseId
                  ? { ...item, quantity: newQuantity }
                  : item
              ),
            };
          }),

        clearCart: () =>
          set(() => ({
            items: [],
            orderId: null,
            isLoading: false,
            error: null,
            discount: {
              type: null,
              value: 0,
              code: null,
              databaseId: null,
            },
            shippingCost: 0,
          })),

        setOrderId: (id) =>
          set(() => ({
            orderId: id,
          })),

        totalQuantity: () => {
          return get().items.reduce((total, item) => total + item.quantity, 0);
        },

        totalPrice: () => {
          // First calculate the subtotal
          const subtotal = get().items.reduce(
            (total, item) => total + item.quantity * (item.price || 0),
            0
          );

          // Ensure subtotal is a valid number
          const validSubtotal = isNaN(subtotal) ? 0 : subtotal;

          const { type, value } = get().discount;

          // If no discount or invalid value, return the subtotal
          if (!type || !value || isNaN(value) || value <= 0) {
            return validSubtotal;
          }

          let finalPrice = validSubtotal;

          if (type === "percentage") {
            // Calculate percentage discount
            const percentage = Math.min(100, Math.max(0, value)); // Ensure percentage is between 0-100
            const discountAmount = validSubtotal * (percentage / 100);
            finalPrice = validSubtotal - discountAmount;
          } else if (type === "fixed") {
            // Calculate fixed discount
            const fixedValue = Math.max(0, value); // Ensure value is not negative
            finalPrice = validSubtotal - fixedValue;
          }

          // Ensure we don't return a negative value
          return Math.max(0, finalPrice);
        },

        discountAmount: () => {
          // First calculate the subtotal
          const subtotal = get().items.reduce(
            (total, item) => total + item.quantity * (item.price || 0),
            0
          );

          // Ensure subtotal is a valid number
          const validSubtotal = isNaN(subtotal) ? 0 : subtotal;

          const { type, value } = get().discount;

          // If no discount or invalid value, return 0
          if (!type || !value || isNaN(value) || value <= 0) {
            return 0;
          }

          let result = 0;
          if (type === "percentage") {
            const percentage = Math.min(100, Math.max(0, value));
            result = validSubtotal * (percentage / 100);
          } else if (type === "fixed") {
            const fixedValue = Math.max(0, value);
            result = fixedValue;
          }

          // Then return the result
          return Math.min(validSubtotal, result);
        },

        applyDiscount: (type, value, code, databaseId) => {
          // Validate the value
          const validValue = isNaN(value) ? 0 : value;

          set(() => ({
            discount: {
              type,
              value: validValue,
              code,
              databaseId,
            },
          }));
        },

        removeDiscount: () =>
          set(() => ({
            discount: { type: null, value: 0, code: null, databaseId: null },
          })),

        setLoading: (loading) => set({ isLoading: loading }),
        setError: (error) => set({ error }),

        calculateShipping: () => {
          const items = get().items;
          const shippingClasses = new Set(
            items.map((item) => item.shippingClassId).filter(Boolean)
          );

          // If no shipping classes, return 0
          if (shippingClasses.size === 0) return 0;

          // Map the shipping class IDs to their costs
          const shippingCosts = Array.from(shippingClasses).map((classId) => {
            switch (classId) {
              case 48:
                return 0; // Frakt 0
              case 49:
                return 50; // Frakt 50
              case 50:
                return 85; // Frakt 85
              case 51:
                return 99; // Frakt 99
              default:
                return 0;
            }
          });

          // Return the highest shipping cost
          return Math.max(...shippingCosts);
        },

        totalWithShipping: () => {
          const subtotal = get().totalPrice();
          const shipping = get().calculateShipping();
          return subtotal + shipping;
        },

        debug: () => {
          const state = get();
          const subtotal = state.items.reduce(
            (total, item) => total + item.quantity * (item.price || 0),
            0
          );

          console.log("Cart Store Debug:", {
            items: state.items,
            subtotal,
            discount: state.discount,
            calculatedTotal: state.totalPrice(),
            calculatedDiscount: state.discountAmount(),
            shippingCost: state.calculateShipping(),
            totalWithShipping: state.totalWithShipping(),
          });
        },
      }),
      {
        name: "cart-storage",
        partialize: (state) => ({
          items: state.items,
          isLoading: state.isLoading,
          error: state.error,
          orderId: state.orderId,
          discount: state.discount,
          shippingCost: state.shippingCost,
        }),
        onRehydrateStorage: () => (state) => {
          if (state) {
            state.isLoading = false;
          }
        },
        storage: {
          getItem: (name) => {
            const str = localStorage.getItem(name);
            if (!str) return null;

            try {
              const data = JSON.parse(str);
              const timestamp = data._timestamp;

              if (timestamp && Date.now() - timestamp > 12 * 60 * 60 * 1000) {
                localStorage.removeItem(name);
                return null;
              }

              delete data._timestamp;
              return data as StorageValue<CartState>;
            } catch {
              return null;
            }
          },
          setItem: (name, value) => {
            const data = { ...value, _timestamp: Date.now() };
            localStorage.setItem(name, JSON.stringify(data));
          },
          removeItem: (name) => localStorage.removeItem(name),
        },
      }
    )
  );
